import { computed, Directive, inject, input } from '@angular/core';
import { DragListDirective } from './drag-list.directive';

/**
 * Per-item directive for drag-and-drop reordering within an `appDragList`.
 *
 * Apply to each draggable child inside an `[appDragList]` container.
 * Handles all host events and CSS class bindings — no drag plumbing needed
 * in the host component template.
 *
 * By default the host element is NOT draggable. Pair with `[appDragHandle]` on
 * a child element to restrict dragging to that handle only.
 *
 * The `.dragging`, `.drag-over-before`, and `.drag-over-after` classes are
 * applied automatically; define their styles in the host component's CSS.
 *
 * @example
 * ```html
 * <ul appDragList (itemReordered)="onReorder($event)">
 *   @for (item of items(); track item.id; let i = $index) {
 *     <li appDragItem [dragItemIndex]="i">
 *       <span appDragHandle>&#8801;</span>
 *       {{ item.label }}
 *     </li>
 *   }
 * </ul>
 * ```
 */
@Directive({
  selector: '[appDragItem]',
  standalone: true,
  host: {
    '[attr.draggable]': 'draggable',
    '[class.dragging]': 'isDragging()',
    '[class.drag-over-before]': 'isDragOverBefore()',
    '[class.drag-over-after]': 'isDragOverAfter()',
    '(dragstart)': 'onDragStart($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(drop)': 'onDrop($event)',
    '(dragend)': 'onDragEnd()',
  },
})
export class DragItemDirective {
  private readonly list = inject(DragListDirective);

  /** The index of this item in the parent list. Must be kept in sync with the `@for` index. */
  readonly dragItemIndex = input.required<number>();

  /** Controlled by DragHandleDirective — only true while a drag gesture may start. */
  draggable = false;

  readonly isDragging = computed(() => this.list.dragIndex() === this.dragItemIndex());

  readonly isDragOverBefore = computed(
    () =>
      this.list.dragOverIndex() === this.dragItemIndex() && this.list.dropPosition() === 'before',
  );

  readonly isDragOverAfter = computed(
    () =>
      this.list.dragOverIndex() === this.dragItemIndex() && this.list.dropPosition() === 'after',
  );

  /** Called by DragHandleDirective on mousedown/touchstart. */
  enableDrag(): void {
    this.draggable = true;
  }

  /** Called by DragHandleDirective on mouseup (no drag started) and on dragend. */
  disableDrag(): void {
    this.draggable = false;
  }

  onDragStart(event: DragEvent): void {
    this.list.onDragStart(event, this.dragItemIndex());
  }

  onDragOver(event: DragEvent): void {
    this.list.onDragOver(event, this.dragItemIndex());
  }

  onDragLeave(event: DragEvent): void {
    this.list.onDragLeave(event);
  }

  onDrop(event: DragEvent): void {
    this.list.onDrop(event, this.dragItemIndex());
  }

  onDragEnd(): void {
    this.disableDrag();
    this.list.onDragEnd();
  }
}
